import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, Sparkles, Database, RefreshCw, Trash2, Calendar, LayoutGrid, Building2, Stethoscope, FileDown, FileSpreadsheet, UploadCloud, TrendingUp, TrendingDown, Minus, GitCompareArrows, Link2 } from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, apiError, fmtXof } from "../../lib/api";
import { HospitalListesLink } from "./HospitalListesLink";
import { AiCompare } from "./AiCompare";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "./DateField";

const CHART_COLORS = ["#0EA5E9", "#0A2540", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6", "#EC4899"];

const PERIODS = [
  ["jour", "Jour"], ["semaine", "Semaine"], ["mois", "Mois"],
  ["trimestre", "Trimestre"], ["semestre", "Semestre"], ["annee", "Année"],
];

// Rendu Markdown léger (titres ##, puces -, gras **)
function Markdown({ text }) {
  const lines = (text || "").split("\n");
  const out = [];
  let bullets = [];
  const flush = (i) => { if (bullets.length) { out.push(<ul key={`u${i}`} className="my-2 ml-4 list-disc space-y-1">{bullets}</ul>); bullets = []; } };
  const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") && p.endsWith("**")
    ? <strong key={j} className="text-navy">{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>);
  lines.forEach((ln, i) => {
    const t = ln.trim();
    if (t === "---" || t === "***") { flush(i); return; }
    if (t.startsWith("## ")) { flush(i); out.push(<h4 key={i} className="mt-4 mb-1 font-display text-base font-semibold text-ciel">{t.slice(3)}</h4>); }
    else if (t.startsWith("### ")) { flush(i); out.push(<h5 key={i} className="mt-3 mb-1 font-semibold text-navy">{t.slice(4)}</h5>); }
    else if (t.startsWith("# ")) { flush(i); out.push(<h4 key={i} className="mt-1 mb-2 font-display text-lg font-bold text-navy">{t.slice(2)}</h4>); }
    else if (t.startsWith("- ") || t.startsWith("* ")) { bullets.push(<li key={i} className="text-sm text-slate-600">{inline(t.slice(2))}</li>); }
    else if (t) { flush(i); out.push(<p key={i} className="my-1 text-sm text-slate-600">{inline(t)}</p>); }
  });
  flush("end");
  return <div data-testid="hospital-ai-content">{out}</div>;
}

function PctBar({ pct }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-ciel" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <span className="w-12 text-right tabular-nums text-slate-500">{pct} %</span>
    </div>
  );
}

function TableCard({ t }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`hospital-table-${t.key}`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-navy">{t.title}</h3>
        {t.type === "cout" && <span className="text-xs font-semibold text-emerald-600">{fmtXof(t.total_montant)}</span>}
        {t.type === "count" && t.total != null && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">Total {t.total}</span>}
      </div>
      {t.rows.length === 0 ? (
        <p className="py-3 text-center text-xs text-slate-400">Aucune donnée</p>
      ) : t.type === "constantes" ? (
        <table className="w-full text-xs"><thead><tr className="text-left text-slate-400">
          <th className="pb-1 font-medium">Constante</th><th className="pb-1 font-medium">Moyenne</th><th className="pb-1 font-medium">Norme</th><th className="pb-1 text-right font-medium">% anormal</th></tr></thead>
          <tbody>{t.rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-50">
              <td className="py-1.5 text-slate-700">{r.label}</td>
              <td className="py-1.5 tabular-nums text-navy">{r.moyenne} <span className="text-slate-400">{r.unite}</span></td>
              <td className="py-1.5 text-slate-400">{r.norme}</td>
              <td className="py-1.5 text-right"><span className={r.pct_anormal >= 30 ? "font-semibold text-red-600" : "text-slate-500"}>{r.pct_anormal} %</span></td>
            </tr>))}</tbody></table>
      ) : t.type === "cout" ? (
        <table className="w-full text-xs"><tbody>{t.rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-50">
            <td className="py-1.5 text-slate-700">{r.label}</td>
            <td className="py-1.5 text-right tabular-nums text-navy">{fmtXof(r.montant)}</td>
            <td className="py-1.5 pl-3"><PctBar pct={r.pourcentage} /></td>
          </tr>))}</tbody></table>
      ) : (
        <table className="w-full text-xs"><tbody>{t.rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-50">
            <td className="py-1.5 text-slate-700">{r.label}</td>
            <td className="py-1.5 text-right tabular-nums text-navy">{r.effectif}</td>
            <td className="py-1.5 pl-3"><PctBar pct={r.pourcentage} /></td>
          </tr>))}</tbody></table>
      )}
      {t.note && <p className="mt-2 text-[11px] text-slate-400">{t.note}</p>}
    </div>
  );
}

function ChartsRow({ tables }) {
  const byKey = (k) => tables.find((t) => t.key === k);
  const sortie = byKey("mode_sortie");
  const admission = byKey("mode_admission");
  const couts = byKey("couts");
  const dp = byKey("dp");
  const pieData = (t) => (t?.rows || []).slice(0, 8).map((r) => ({ name: r.label, value: r.effectif }));
  const dpData = (dp?.rows || []).slice(0, 6).map((r) => ({ name: r.label, value: r.effectif }));
  const coutData = (couts?.rows || []).map((r) => ({ name: r.label, value: r.montant }));

  const Card = ({ title, children }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="mb-2 font-display text-sm font-semibold text-navy">{title}</h3>
      <div className="h-56">{children}</div>
    </div>
  );

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="hospital-charts">
      <Card title="Mode de sortie">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={pieData(sortie)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.value}`}>
              {pieData(sortie).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
      <Card title="Mode d'admission">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={pieData(admission)} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={38} outerRadius={70} label={(e) => `${e.value}`}>
              {pieData(admission).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
      <Card title="Coûts par poste (FCFA)">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={coutData} layout="vertical" margin={{ left: 8, right: 8 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 9 }} />
            <Tooltip formatter={(v) => fmtXof(v)} />
            <Bar dataKey="value" fill="#0EA5E9" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card title="Top pathologies (DP)">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dpData} layout="vertical" margin={{ left: 8, right: 8 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 9 }} />
            <Tooltip />
            <Bar dataKey="value" fill="#0A2540" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}

export function HospitalCareManager({ module = "soins", title = "Résultat des soins" }) {
  const [secteurs, setSecteurs] = useState([]);
  const [period, setPeriod] = useState("mois");
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [view, setView] = useState("global");
  const [secteur, setSecteur] = useState("");
  const [service, setService] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [ai, setAi] = useState({ status: "none", analysis: "", period_label: "", generated_at: "" });
  const [aiBusy, setAiBusy] = useState(false);
  const pollRef = useRef(null);

  const [listesInfo, setListesInfo] = useState(null);
  const [showLink, setShowLink] = useState(false);
  const loadListes = useCallback(() => api.get(`/admin/hospital/listes?module=${module}`).then(({ data }) => { setSecteurs(data.secteurs || []); setListesInfo(data); }).catch(() => {}), [module]);
  useEffect(() => {
    loadListes();
    const t = setInterval(loadListes, 60000);
    api.get(`/admin/hospital/ai-analysis?module=${module}`).then(({ data }) => setAi(data)).catch(() => {});
    return () => clearInterval(t);
  }, [module, loadListes]);

  const effSecteur = view === "global" ? "" : secteur;
  const effService = view === "service" ? service : "";

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ module, period });
    if (period === "custom") { p.set("frm", frm); p.set("to", to); }
    if (effSecteur) p.set("secteur", effSecteur);
    if (effService) p.set("service", effService);
    api.get(`/admin/hospital/summary?${p.toString()}`).then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Chargement impossible"))).finally(() => setLoading(false));
  }, [module, period, frm, to, effSecteur, effService]);

  useEffect(() => {
    if (period === "custom" && (!frm || !to)) return;
    load();
  }, [load, period, frm, to]);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/hospital/seed", { module, count: 400 });
      toast.success(`${data.inserted} séjours de démonstration générés`);
      load();
    } catch (e) { toast.error(apiError(e, "Génération impossible")); } finally { setSeeding(false); }
  };

  const clearData = async () => {
    if (!window.confirm("Supprimer toutes les données de ce module ?")) return;
    try { await api.delete(`/admin/hospital/records?module=${module}`); toast.success("Données supprimées"); load(); }
    catch (e) { toast.error(apiError(e, "Suppression impossible")); }
  };

  const [exporting, setExporting] = useState("");
  const [freshAI, setFreshAI] = useState(false);

  const refreshAIAndWait = async () => {
    const body = { module, period, secteur: effSecteur, service: effService };
    if (period === "custom") { body.frm = frm; body.to = to; }
    toast.info("Régénération de l'analyse IA avant export…");
    await api.post("/admin/hospital/ai-analysis", body);
    setAi((a) => ({ ...a, status: "running" }));
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      const { data } = await api.get(`/admin/hospital/ai-analysis?module=${module}`);
      if (data.status !== "running") { setAi(data); return; }
    }
  };

  const exportFile = async (kind) => {
    setExporting(kind);
    try {
      if (kind === "pdf" && freshAI) await refreshAIAndWait();
      const res = await api.get(`/admin/hospital/export/${kind}?${params().toString()}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `resultat-soins-${module}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch (e) { toast.error(apiError(e, "Export impossible")); } finally { setExporting(""); }
  };

  const [archiving, setArchiving] = useState(false);
  const params = useCallback(() => {
    const p = new URLSearchParams({ module, period });
    if (period === "custom") { p.set("frm", frm); p.set("to", to); }
    if (effSecteur) p.set("secteur", effSecteur);
    if (effService) p.set("service", effService);
    return p;
  }, [module, period, frm, to, effSecteur, effService]);
  const archiveDrive = async () => {
    setArchiving(true);
    try {
      if (freshAI) await refreshAIAndWait();
      const { data } = await api.post(`/admin/hospital/archive-drive?${params().toString()}`);
      toast.success(`Rapport archivé dans Google Drive › ${data.folder}`);
    } catch (e) { toast.error(apiError(e, "Archivage impossible")); } finally { setArchiving(false); }
  };

  const [cmp, setCmp] = useState(null);
  const [cmpBusy, setCmpBusy] = useState(false);
  const runCompare = async () => {
    setCmpBusy(true);
    try {
      const { data } = await api.get(`/admin/hospital/compare?${params().toString()}`);
      setCmp(data);
    } catch (e) { toast.error(apiError(e, "Comparaison impossible")); } finally { setCmpBusy(false); }
  };

  const [autoCfg, setAutoCfg] = useState(null);
  const [alertCfg, setAlertCfg] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [alertBusy, setAlertBusy] = useState(false);
  const loadAutomation = useCallback(() => {
    api.get(`/admin/hospital/auto-archive?module=${module}`).then(({ data }) => setAutoCfg(data)).catch(() => {});
    api.get(`/admin/hospital/alerts?module=${module}`).then(({ data }) => setAlertCfg(data)).catch(() => {});
  }, [module]);
  useEffect(() => { loadAutomation(); }, [loadAutomation]);
  const saveAuto = async (patch) => {
    const next = { ...autoCfg, ...patch }; setAutoCfg(next);
    try { await api.put("/admin/hospital/auto-archive", { module, enabled: next.enabled, frequency: next.frequency, fresh_ai: next.fresh_ai }); toast.success("Archivage automatique enregistré"); }
    catch (e) { toast.error(apiError(e, "Enregistrement impossible")); }
  };
  const archiveNow = async () => {
    setAutoBusy(true);
    try {
      const { data } = await api.post(`/admin/hospital/auto-archive/run-now?module=${module}`);
      if (data.archived) toast.success(`Rapport ${data.period} archivé dans Drive › Rapports`);
      else toast.info(data.error || data.skipped || "Rien à archiver");
      loadAutomation();
    } catch (e) { toast.error(apiError(e, "Archivage impossible")); } finally { setAutoBusy(false); }
  };
  const saveAlerts = async (patch) => {
    const next = { ...alertCfg, ...patch }; setAlertCfg(next);
    try { await api.put("/admin/hospital/alerts", { module, enabled: next.enabled, email: next.email, thresholds: next.thresholds }); toast.success("Alertes enregistrées"); }
    catch (e) { toast.error(apiError(e, "Enregistrement impossible")); }
  };
  const checkNow = async () => {
    setAlertBusy(true);
    try {
      const { data } = await api.post(`/admin/hospital/alerts/check-now?module=${module}`);
      if (data.breaches?.length) toast.warning(`${data.breaches.length} seuil(s) dépassé(s)${data.sent ? " · email envoyé" : ""}`);
      else toast.success("Aucun seuil dépassé sur la période");
    } catch (e) { toast.error(apiError(e, "Vérification impossible")); } finally { setAlertBusy(false); }
  };

  const pollAi = useCallback(() => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/hospital/ai-analysis?module=${module}`);
        setAi(data);
        if (data.status !== "running") { clearInterval(pollRef.current); setAiBusy(false); }
      } catch { /* continue */ }
    }, 4000);
  }, [module]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runAi = async () => {
    setAiBusy(true);
    try {
      const body = { module, period, secteur: effSecteur, service: effService };
      if (period === "custom") { body.frm = frm; body.to = to; }
      const { data } = await api.post("/admin/hospital/ai-analysis", body);
      setAi((a) => ({ ...a, status: "running" }));
      if (data.status === "running") pollAi();
      else setAiBusy(false);
    } catch (e) { toast.error(apiError(e, "Analyse impossible")); setAiBusy(false); }
  };

  const currentSecteur = secteurs.find((s) => s.secteur === secteur);

  return (
    <div className="space-y-5" data-testid={`hospital-manager-${module}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Stethoscope className="text-ciel" size={22} />
        <div>
          <h2 className="font-display text-lg font-bold text-navy">{title}</h2>
          <p className="text-xs text-slate-500">
            {data ? `${data.count} séjour(s) · ${data.period_label}` : "—"}
            {data?.range && ` (${data.range.from} → ${data.range.to})`}
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="hospital-refresh" className="rounded-full text-xs">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </Button>
          <Button size="sm" variant="outline" onClick={seed} disabled={seeding} data-testid="hospital-seed" className="rounded-full text-xs">
            {seeding ? <Loader2 size={13} className="animate-spin" /> : <><Database size={13} className="mr-1" /> Données démo</>}
          </Button>
          {data?.count > 0 && (
            <>
              <Button size="sm" variant="outline" onClick={() => exportFile("pdf")} disabled={exporting === "pdf"} data-testid="hospital-export-pdf" className="rounded-full text-xs">
                {exporting === "pdf" ? <Loader2 size={13} className="animate-spin" /> : <><FileDown size={13} className="mr-1" /> PDF</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => exportFile("excel")} disabled={exporting === "excel"} data-testid="hospital-export-excel" className="rounded-full text-xs">
                {exporting === "excel" ? <Loader2 size={13} className="animate-spin" /> : <><FileSpreadsheet size={13} className="mr-1" /> Excel</>}
              </Button>
              <Button size="sm" variant="outline" onClick={archiveDrive} disabled={archiving} data-testid="hospital-archive-drive" className="rounded-full text-xs">
                {archiving ? <Loader2 size={13} className="animate-spin" /> : <><UploadCloud size={13} className="mr-1" /> Archiver Drive</>}
              </Button>
            </>
          )}
          {data?.count > 0 && (
            <Button size="sm" variant="ghost" onClick={clearData} data-testid="hospital-clear" className="rounded-full text-xs text-red-500 hover:bg-red-50">
              <Trash2 size={13} />
            </Button>
          )}
        </div>
      </div>

      {/* Barre de filtres période */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3" data-testid="hospital-period-bar">
        <Calendar size={15} className="text-slate-400" />
        {PERIODS.map(([k, lbl]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`hospital-period-${k}`}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${period === k ? "bg-ciel text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {lbl}
          </button>
        ))}
        <div className="flex items-center gap-1">
          <DateField value={frm} onChange={(v) => { setFrm(v); setPeriod("custom"); }} placeholder="Date de début" testid="hospital-date-from" />
          <span className="text-xs text-slate-400">→</span>
          <DateField value={to} onChange={(v) => { setTo(v); setPeriod("custom"); }} placeholder="Date de fin" testid="hospital-date-to" />
          <Button size="sm" onClick={load} disabled={period === "custom" && (!frm || !to)} className="h-8 rounded-full bg-navy px-3 text-xs text-white" data-testid="hospital-date-apply">Appliquer</Button>
        </div>
      </div>

      {/* Sélecteur de vues */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3" data-testid="hospital-view-bar">
        <span className="text-xs font-medium text-slate-500">Vue :</span>
        {[["global", "Global", LayoutGrid], ["secteur", "Par secteur", Building2], ["service", "Par service", Stethoscope]].map(([k, lbl, Icon]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`hospital-view-${k}`}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${view === k ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            <Icon size={12} /> {lbl}
          </button>
        ))}
        {view !== "global" && (
          <select value={secteur} onChange={(e) => { setSecteur(e.target.value); setService(""); }} data-testid="hospital-secteur-select"
            className="h-8 rounded-lg border border-slate-200 px-2 text-xs text-slate-700">
            <option value="">— Choisir un secteur —</option>
            {secteurs.map((s) => <option key={s.secteur} value={s.secteur}>{s.secteur}</option>)}
          </select>
        )}
        {view === "service" && currentSecteur && currentSecteur.services.length > 0 && (
          <select value={service} onChange={(e) => setService(e.target.value)} data-testid="hospital-service-select"
            className="h-8 rounded-lg border border-slate-200 px-2 text-xs text-slate-700">
            <option value="">— Choisir un service —</option>
            {currentSecteur.services.map((sv) => <option key={sv} value={sv}>{sv}</option>)}
          </select>
        )}
        {view === "service" && currentSecteur && currentSecteur.services.length === 0 && (
          <span className="rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-700" data-testid="hospital-service-empty">Aucun service disponible</span>
        )}
        {listesInfo && listesInfo.source !== "defaut" && (
          <button type="button" onClick={() => setShowLink((v) => !v)} data-testid="hospital-link-toggle" className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${showLink ? "bg-[#0E7C7B] text-white" : "bg-teal-50 text-[#0E7C7B] hover:bg-teal-100"}`}><Link2 size={12} /> Lier secteurs ↔ services{listesInfo.mapping === "local" ? " (liaison locale)" : listesInfo.mapping === "pvc" ? " (PVC)" : ""}</button>
        )}
        {view !== "global" && listesInfo && (
          <span className="text-[10px] text-slate-400" data-testid="hospital-listes-source" title={`Source : Patient Vitalis Care — Paramétrage → Gérer les listes (${listesInfo.pvc_url || ""}). Rafraîchissement automatique toutes les 60 s.`}>
            Listes PVC {listesInfo.source === "pvc" || listesInfo.source === "cache" ? "synchronisées" : listesInfo.source === "dernier_connu" ? "(dernière version connue — PVC injoignable)" : "(indisponibles — listes par défaut)"}{listesInfo.fetched_at ? ` · ${new Date(listesInfo.fetched_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : ""}
          </span>
        )}
      </div>
      {showLink && listesInfo && <HospitalListesLink info={listesInfo} onSaved={loadListes} onClose={() => setShowLink(false)} />}

      {/* Analyse IA */}
      <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4" data-testid="hospital-ai-panel">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles size={16} className="text-violet-600" />
          <h3 className="font-display text-sm font-semibold text-navy">Analyse IA & actions correctives</h3>
          {ai.status === "done" && ai.generated_at && <span className="text-[11px] text-slate-400">Générée · {ai.period_label}</span>}
          <Button size="sm" onClick={runAi} disabled={aiBusy || ai.status === "running" || !(data?.count > 0)} data-testid="hospital-ai-run"
            className="ml-auto rounded-full bg-violet-600 px-3 text-xs text-white hover:bg-violet-700">
            {(aiBusy || ai.status === "running") ? <><Loader2 size={13} className="mr-1 animate-spin" /> Analyse en cours…</> : <><Sparkles size={13} className="mr-1" /> Analyser avec l'IA</>}
          </Button>
          <AiCompare moduleKey={`hospital:${module}`} params={{ period, frm, to, secteur: effSecteur, service: effService }} />
        </div>
        {ai.status === "running" && <p className="mt-2 text-xs text-slate-500">L'IA agrège les données et rédige les actions correctives (~30 s)…</p>}
        <label className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="hospital-ai-fresh-toggle">
          <input type="checkbox" checked={freshAI} onChange={(e) => setFreshAI(e.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300 accent-violet-600" data-testid="hospital-ai-fresh-checkbox" />
          Régénérer l'analyse IA juste avant chaque export PDF / archivage Drive (toujours la version la plus fraîche)
        </label>
        {ai.status === "error" && <p className="mt-2 text-xs text-red-500">Erreur : {ai.error}</p>}
        {ai.status === "done" && ai.analysis && <div className="mt-3 rounded-xl bg-white p-4"><Markdown text={ai.analysis} /></div>}
        {ai.status === "none" && <p className="mt-2 text-xs text-slate-400">Lancez l'analyse pour obtenir une synthèse et des actions correctives priorisées basées sur les données agrégées.</p>}
      </div>

      {/* Comparaison de périodes */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="hospital-compare-panel">
        <div className="flex flex-wrap items-center gap-2">
          <GitCompareArrows size={16} className="text-ciel" />
          <h3 className="font-display text-sm font-semibold text-navy">Comparaison avec la période précédente</h3>
          {cmp && <span className="text-[11px] text-slate-400">{cmp.current_range.from}→{cmp.current_range.to} vs {cmp.previous_range.from}→{cmp.previous_range.to}</span>}
          <Button size="sm" onClick={runCompare} disabled={cmpBusy || !(data?.count > 0)} data-testid="hospital-compare-run"
            className="ml-auto rounded-full bg-ciel px-3 text-xs text-white hover:bg-sky-700">
            {cmpBusy ? <Loader2 size={13} className="animate-spin" /> : <><GitCompareArrows size={13} className="mr-1" /> Comparer</>}
          </Button>
        </div>
        {cmp && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="hospital-compare-kpis">
            {cmp.kpis.map((k) => {
              const up = k.delta_pct > 0, down = k.delta_pct < 0;
              const good = k.better === "up" ? up : k.better === "down" ? down : null;
              const color = good === null ? "text-slate-500" : good ? "text-emerald-600" : "text-red-600";
              const Arrow = up ? TrendingUp : down ? TrendingDown : Minus;
              const val = k.unit === "fcfa" ? fmtXof(k.current) : `${k.current}${k.unit === "%" ? " %" : ""}`;
              const prev = k.unit === "fcfa" ? fmtXof(k.previous) : `${k.previous}${k.unit === "%" ? " %" : ""}`;
              return (
                <div key={k.key} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid={`hospital-kpi-${k.key}`}>
                  <p className="text-[11px] text-slate-500">{k.label}</p>
                  <p className="mt-0.5 font-display text-lg font-bold text-navy">{val}</p>
                  <div className={`mt-1 flex items-center gap-1 text-xs font-medium ${color}`}>
                    <Arrow size={13} /> {k.delta_pct > 0 ? "+" : ""}{k.delta_pct} %
                    <span className="font-normal text-slate-400">vs {prev}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!cmp && <p className="mt-2 text-xs text-slate-400">Comparez les indicateurs clés (mortalité, complications, coûts…) avec la période équivalente précédente.</p>}
      </div>

      {/* Automatisations : archivage Drive + alertes seuils */}
      <div className="grid gap-4 md:grid-cols-2" data-testid="hospital-automation">
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="hospital-autoarchive-panel">
          <div className="mb-2 flex items-center gap-2">
            <UploadCloud size={16} className="text-ciel" />
            <h3 className="font-display text-sm font-semibold text-navy">Archivage Drive automatique</h3>
            <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={!!autoCfg?.enabled} onChange={(e) => saveAuto({ enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 accent-ciel" data-testid="hospital-autoarchive-enabled" /> Activer
            </label>
          </div>
          <p className="text-xs text-slate-500">Dépose le PDF « Résultat des soins » dans le dossier <b>Rapports</b> de votre Drive, à chaque fin de période.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Fréquence :</span>
            <select value={autoCfg?.frequency || "mensuel"} onChange={(e) => saveAuto({ frequency: e.target.value })}
              className="h-8 rounded-lg border border-slate-200 px-2 text-xs" data-testid="hospital-autoarchive-frequency">
              <option value="mensuel">Mensuel</option><option value="trimestriel">Trimestriel</option>
            </select>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={!!autoCfg?.fresh_ai} onChange={(e) => saveAuto({ fresh_ai: e.target.checked })}
                className="h-3.5 w-3.5 rounded border-slate-300 accent-violet-600" data-testid="hospital-autoarchive-freshai" /> Inclure une IA fraîche
            </label>
            <Button size="sm" variant="outline" onClick={archiveNow} disabled={autoBusy} data-testid="hospital-autoarchive-now" className="rounded-full text-xs">
              {autoBusy ? <Loader2 size={13} className="animate-spin" /> : "Archiver maintenant"}
            </Button>
          </div>
          {autoCfg?.last_archived && <p className="mt-2 text-[11px] text-slate-400">Dernier archivage : {autoCfg.last_archived}{autoCfg.last_archived_at ? ` · ${new Date(autoCfg.last_archived_at).toLocaleString("fr-FR")}` : ""}</p>}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="hospital-alerts-panel">
          <div className="mb-2 flex items-center gap-2">
            <TrendingUp size={16} className="text-red-500" />
            <h3 className="font-display text-sm font-semibold text-navy">Alertes sur seuils critiques</h3>
            <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={!!alertCfg?.enabled} onChange={(e) => saveAlerts({ enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 accent-red-500" data-testid="hospital-alerts-enabled" /> Activer
            </label>
          </div>
          <p className="text-xs text-slate-500">Envoie un email d'alerte quand un indicateur du mois dépasse son seuil.</p>
          <div className="mt-2">
            <Input type="email" placeholder="email@destinataire" value={alertCfg?.email || ""}
              onChange={(e) => setAlertCfg((c) => ({ ...c, email: e.target.value }))} onBlur={(e) => saveAlerts({ email: e.target.value })}
              className="h-8 text-xs" data-testid="hospital-alerts-email" />
          </div>
          <div className="mt-3 space-y-1.5">
            {alertCfg && Object.entries(alertCfg.labels || {}).map(([k, lbl]) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                <span className="flex-1 text-slate-600">{lbl}</span>
                <span className="text-slate-400">seuil</span>
                <Input type="number" step="0.1" value={alertCfg.thresholds?.[k] ?? ""}
                  onChange={(e) => setAlertCfg((c) => ({ ...c, thresholds: { ...c.thresholds, [k]: e.target.value } }))}
                  onBlur={() => saveAlerts({ thresholds: alertCfg.thresholds })}
                  className="h-7 w-20 text-center text-xs" data-testid={`hospital-alerts-th-${k}`} />
                <span className="text-slate-400">%</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={checkNow} disabled={alertBusy} data-testid="hospital-alerts-check" className="rounded-full text-xs">
              {alertBusy ? <Loader2 size={13} className="animate-spin" /> : "Vérifier maintenant"}
            </Button>
            {alertCfg?.last_alert && <span className="text-[11px] text-slate-400">Dernière alerte envoyée</span>}
          </div>
        </div>
      </div>

      {/* Tableaux de synthèse */}
      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-ciel" /></div>
      ) : data?.count === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-12 text-center" data-testid="hospital-empty">
          <Database size={36} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucune donnée sur cette période{effSecteur ? ` pour ${effSecteur}` : ""}.</p>
          <Button onClick={seed} disabled={seeding} className="mt-4 rounded-full bg-ciel text-white hover:bg-sky-700" data-testid="hospital-empty-seed">
            {seeding ? <Loader2 size={14} className="animate-spin" /> : "Générer des données de démonstration"}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {data?.tables && <ChartsRow tables={data.tables} />}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="hospital-tables">
            {data?.tables.map((t) => <TableCard key={t.key} t={t} />)}
          </div>
        </div>
      )}
    </div>
  );
}
